import { Component, OnInit, OnDestroy, computed, signal } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AuthenticationService } from "../../shared/services/authentication.service";
import { MeetDayService } from "../../api/services/meet-day.service";
import { StudentService } from "../../shared/services/student.service";
import { CompanyService } from "../../api/services/company.service";
import { CompanyService as SharedCompanyService } from "../../shared/services/company.service";
import { forkJoin, Subscription, of } from "rxjs";
import { catchError, finalize, switchMap } from "rxjs/operators";
import { RoleTypes } from "../../guards/roletypes";
import { MeetDay } from "../../api/models/meet-day";
import { MeetDayStudent } from "../../api/models/meet-day-student";
import { Company } from "../../api/models/company";
import { ModelStudentprofile } from "../../model/model.studentprofile";
import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { LoaderComponent } from "../../core/loader/loader.component";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
  faCheckCircle,
  faExclamationTriangle,
  faCalendar,
  faMapMarkerAlt,
  faHome,
} from "@fortawesome/free-solid-svg-icons";
import { StudentMeetdayQrComponent } from "../../student/student-meetday-qr/student-meetday-qr.component";

@Component({
  selector: "app-meetday-attendance-confirmation",
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    LoaderComponent,
    FontAwesomeModule,
    StudentMeetdayQrComponent,
  ],
  template: `
    <div class="d-flex align-items-center bg-light py-5">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-md-8 col-lg-6">
            <!-- Student View (No QR params or not admin/company) -->
            @if (showStudentLanding()) {
            <!-- Loading State -->
            @if (studentIsLoading()) {
            <div class="text-center">
              <div class="card border-0 shadow-sm">
                <div class="card-body p-5">
                  <div
                    class="spinner-border text-primary mb-3"
                    role="status"
                  ></div>
                  <h4 class="mt-3 text-muted">
                    {{ "Student Loading Registration" | translate }}
                  </h4>
                </div>
              </div>
            </div>
            }

            <!-- Error State -->
            @else if (studentError()) {
            <div class="text-center">
              <div class="card border-0 shadow">
                <div class="card-header bg-danger text-white py-4">
                  <div class="d-flex align-items-center justify-content-center">
                    <div>
                      <h2 class="mb-0 fw-bold">{{ "Error" | translate }}</h2>
                      <p class="mb-0 opacity-75">
                        {{ "Something went wrong" | translate }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="card-body p-5 text-center">
                  <p class="text-muted mb-5 fs-5">
                    {{ studentError() }}
                  </p>
                  <a routerLink="/" class="btn btn-secondary btn-lg px-5 py-3">
                    <fa-icon [icon]="faHome" class="me-2"></fa-icon>
                    {{ "Return to Homepage" | translate }}
                  </a>
                </div>
              </div>
            </div>
            }

            <!-- Success State -->
            @else {
            <div class="text-center">
              <div class="card border-0 shadow">
                <div class="card-header bg-primary text-white py-2">
                  <div class="d-flex align-items-center justify-content-center">
                    <div>
                      <h2
                        class="mb-0 fw-bold text-light"
                        style="font-size: 1.25rem;"
                      >
                        {{ "Student Registration Confirmed" | translate }}
                      </h2>
                      <p class="mb-0 opacity-75 small">
                        {{ studentMeetDay()?.name }}
                      </p>
                    </div>
                  </div>
                </div>

                <div class="card-body p-3">
                  @if (studentDisplayName()) {
                  <p class="text-muted mb-2 text-center small">
                    {{
                      "Student QR Instructions"
                        | translate : { name: studentDisplayName() }
                    }}
                  </p>
                  }

                  <div class="mb-3">
                    <div class="row justify-content-center">
                      <div class="col-auto">
                        <div class="bg-white border rounded-3 shadow-sm p-3">
                          <div
                            *ngIf="studentMeetDay()?.date"
                            class="d-flex align-items-center mb-2"
                          >
                            <div
                              class="text-primary mr-2 d-flex justify-content-center"
                            >
                              <fa-icon
                                [icon]="faCalendar"
                                class="small"
                              ></fa-icon>
                            </div>
                            <div class="fw-medium small">
                              {{ studentMeetDay()?.date | date : "fullDate" }}
                              <span class="text-muted">
                                {{
                                  studentMeetDay()?.date | date : "shortTime"
                                }}
                              </span>
                            </div>
                          </div>

                          <div
                            *ngIf="studentMeetDay()?.address"
                            class="d-flex align-items-center"
                          >
                            <div
                              class="text-primary mr-2 d-flex justify-content-center"
                            >
                              <fa-icon
                                [icon]="faMapMarkerAlt"
                                class="small"
                              ></fa-icon>
                            </div>
                            <div class="fw-medium small">
                              {{ studentMeetDay()?.address?.street }}
                              {{ studentMeetDay()?.address?.number }},
                              <span class="text-muted">
                                {{ studentMeetDay()?.address?.postalCode }}
                                {{ studentMeetDay()?.address?.city }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-center mb-3">
                    <app-student-meetday-qr
                      [meetDayId]="studentMeetDayId()"
                      [studentId]="studentProfileId()"
                    ></app-student-meetday-qr>
                  </div>
                </div>
              </div>
            </div>
            } }

            <!-- Company View -->
            @else {
            <!-- Loading State -->
            @if (companyIsLoading()) {
            <div class="text-center">
              <div class="card border-0 shadow-sm">
                <div class="card-body p-5">
                  <app-loader></app-loader>
                  <h4 class="mt-3 text-muted">
                    {{ "Processing attendance confirmation..." | translate }}
                  </h4>
                </div>
              </div>
            </div>
            }

            <!-- Success State -->
            @else if (companyIsSuccess()) {
            <div class="text-center">
              <div class="card border-0 shadow">
                <div class="card-header bg-primary text-white py-4">
                  <div class="d-flex align-items-center justify-content-center">
                    <div>
                      <h2
                        class="mb-0 fw-bold text-light"
                        *ngIf="!isCompanyFlow()"
                      >
                        {{ "Attendance Confirmed!" | translate }}
                      </h2>
                      <h2
                        class="mb-0 fw-bold text-light"
                        *ngIf="isCompanyFlow()"
                      >
                        {{ "Connection Established!" | translate }}
                      </h2>
                    </div>
                  </div>
                </div>

                <div class="card-body p-4">
                  <!-- Student Information -->
                  <div class="mb-3" *ngIf="student()">
                    <div class="text-center">
                      <div class="mb-3" *ngIf="student()?.avatarUri">
                        <img
                          [src]="student()!.avatarUri"
                          class="rounded-circle border shadow-sm"
                          style="width: 80px; height: 80px; object-fit: cover;"
                          [alt]="
                            student()!.firstName + ' ' + student()!.lastName
                          "
                        />
                      </div>
                      <div>
                        <h4 class="mb-2 fw-bold">
                          {{ student()?.firstName }} {{ student()?.lastName }}
                        </h4>
                        <p
                          class="mb-1 text-muted"
                          *ngIf="
                            student()?.educations &&
                            student()?.educations.length > 0
                          "
                        >
                          {{ student()?.educations[0]?.programName }}
                        </p>
                        <p
                          class="mb-0 text-muted"
                          *ngIf="
                            student()?.educations &&
                            student()?.educations.length > 0
                          "
                        >
                          {{ student()?.educations[0]?.schoolName }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Meetday Information -->
                  <div *ngIf="meetDay()" class="mb-5">
                    <div class="text-center mb-3">
                      <h3 class="text-primary fw-bold mb-4">
                        {{ meetDay()?.name }}
                      </h3>
                    </div>

                    <div class="row justify-content-center">
                      <div class="col-auto">
                        <div class="bg-white border rounded-3 shadow-sm p-4">
                          <div
                            *ngIf="meetDay()?.date"
                            class="d-flex align-items-center mb-3"
                          >
                            <div
                              class="text-primary mr-3 d-flex justify-content-center"
                            >
                              <fa-icon [icon]="faCalendar"></fa-icon>
                            </div>
                            <div class="fw-medium">
                              {{ meetDay()?.date | date : "fullDate" }}
                            </div>
                          </div>

                          <div
                            *ngIf="meetDay()?.address"
                            class="d-flex align-items-center"
                          >
                            <div
                              class="text-primary mr-3 d-flex justify-content-center"
                            >
                              <fa-icon [icon]="faMapMarkerAlt"></fa-icon>
                            </div>
                            <div class="fw-medium">
                              {{ meetDay()?.address?.street }}
                              {{ meetDay()?.address?.number }},
                              <span class="text-muted"
                                >{{ meetDay()?.address?.postalCode }}
                                {{ meetDay()?.address?.city }}</span
                              >
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Thank you message and action -->
                  <div class="text-center">
                    <p class="text-muted mb-4" *ngIf="!isCompanyFlow()">
                      {{
                        "Thank you for confirming this student's attendance!"
                          | translate
                      }}
                    </p>
                    <p class="text-muted mb-4" *ngIf="isCompanyFlow()">
                      {{
                        "You are now connected with this student for this event. You can view their profile in your student connections."
                          | translate
                      }}
                    </p>
                    <a
                      routerLink="/"
                      class="btn btn-primary btn-lg px-5 py-3"
                      *ngIf="!isCompanyFlow()"
                    >
                      <fa-icon [icon]="faHome" class="me-2"></fa-icon>
                      {{ "Return to Homepage" | translate }}
                    </a>
                    <a
                      routerLink="/company/students"
                      class="btn btn-primary btn-lg px-5 py-3"
                      *ngIf="isCompanyFlow()"
                    >
                      <fa-icon [icon]="faHome" class="me-2"></fa-icon>
                      {{ "View Student Connections" | translate }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
            }

            <!-- Error State -->
            @else {
            <div class="text-center">
              <div class="card border-0 shadow">
                <div class="card-header bg-danger text-white py-4">
                  <div class="d-flex align-items-center justify-content-center">
                    <fa-icon
                      [icon]="faExclamationTriangle"
                      class="fs-1 me-3"
                    ></fa-icon>
                    <div>
                      <h2 class="mb-0 fw-bold">{{ "Error" | translate }}</h2>
                      <p class="mb-0 opacity-75">
                        {{ "Something went wrong" | translate }}
                      </p>
                    </div>
                  </div>
                </div>

                <div class="card-body p-5 text-center">
                  <p class="text-muted mb-5 fs-5">
                    {{ companyErrorMessage() | translate }}
                  </p>
                  <a routerLink="/" class="btn btn-secondary btn-lg px-5 py-3">
                    <fa-icon [icon]="faHome" class="me-2"></fa-icon>
                    {{ "Return to Homepage" | translate }}
                  </a>
                </div>
              </div>
            </div>
            } }
          </div>
        </div>
      </div>
    </div>
  `,
})
export class MeetdayAttendanceConfirmationComponent
  implements OnInit, OnDestroy
{
  // Company view signals
  readonly companyIsLoading = signal(true);
  readonly companyIsSuccess = signal(false);
  readonly companyErrorMessage = signal("");
  readonly isCompanyFlow = signal(false);
  readonly meetDay = signal<MeetDay | null>(null);
  readonly student = signal<ModelStudentprofile | null>(null);
  readonly showStudentLanding = signal(false);

  // Student view signals
  readonly studentIsLoading = signal(true);
  readonly studentError = signal<string | null>(null);
  readonly studentMeetDay = signal<MeetDay | null>(null);
  readonly studentMeetDayStudent = signal<MeetDayStudent | null>(null);
  readonly studentProfile = signal<ModelStudentprofile | null>(null);
  readonly studentMeetDayId = signal<string>("");
  readonly studentProfileId = signal<string>("");

  readonly studentChosenCompanies = computed<Company[]>(() => {
    return this.studentMeetDayStudent()?.chosenCompanies ?? [];
  });

  readonly studentHasChosenCompanies = computed(
    () => this.studentChosenCompanies().length > 0
  );

  readonly studentDisplayName = computed(() => {
    const profile = this.studentProfile();
    if (profile) {
      return `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim();
    }

    const anonymous = this.studentMeetDayStudent()?.anonymousMeetdayStudent;
    if (anonymous) {
      return `${anonymous.firstName ?? ""} ${anonymous.lastName ?? ""}`.trim();
    }

    return null;
  });

  faCheckCircle = faCheckCircle;
  faExclamationTriangle = faExclamationTriangle;
  faCalendar = faCalendar;
  faMapMarkerAlt = faMapMarkerAlt;
  faHome = faHome;

  private subscriptions = new Subscription();
  private companyFlowInitialized = false;
  private companyId: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private authService: AuthenticationService,
    private meetDayService: MeetDayService,
    private studentService: StudentService,
    private companyService: CompanyService,
    private sharedCompanyService: SharedCompanyService
  ) {}

  ngOnInit(): void {
    const meetDayId = this.route.snapshot.paramMap.get("meetDayId");
    const studentId = this.route.snapshot.queryParamMap.get("studentId");

    // Check if user is authenticated as admin or company
    // Note: profile$ now waits for authentication to complete, preventing race conditions
    this.subscriptions.add(
      this.authService.profile$.subscribe((profile) => {
        const roles = profile?.roles ?? [];
        const isAdmin = roles.includes(RoleTypes.Beheerder);
        const isCompany = roles.includes(RoleTypes.Company);

        // Show company view for admin/company users, student view otherwise
        if (isAdmin || isCompany) {
          this.showStudentLanding.set(false);

          if (this.companyFlowInitialized) {
            return;
          }

          this.companyFlowInitialized = true;
          this.isCompanyFlow.set(isCompany);

          if (isCompany) {
            this.loadCompanyProfile();
          } else {
            this.processAttendanceConfirmation();
          }
        } else {
          // User is not authenticated as admin/company, show student view
          this.showStudentLanding.set(true);
          this.loadStudentView(meetDayId, studentId);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private loadStudentView(
    meetDayId: string | null,
    studentId: string | null
  ): void {
    if (!meetDayId || !studentId) {
      this.studentError.set("Student Registration Link Incomplete");
      this.studentIsLoading.set(false);
      return;
    }

    this.studentMeetDayId.set(meetDayId);
    this.studentProfileId.set(studentId);

    this.studentIsLoading.set(true);
    this.studentError.set(null);

    this.subscriptions.add(
      forkJoin({
        meetDay: this.meetDayService.getMeetDay({ id: meetDayId }),
        studentProfile: this.studentService
          .apiGetStudentprofile(studentId)
          .pipe(catchError(() => of(null))),
      })
        .pipe(
          switchMap(({ meetDay, studentProfile }) => {
            this.studentMeetDay.set(meetDay);

            if (studentProfile) {
              this.studentProfile.set(studentProfile as ModelStudentprofile);
            }

            return this.meetDayService
              .getMeetDayStudent({ meetDayId, id: studentId })
              .pipe(catchError(() => of(null)));
          }),
          finalize(() => this.studentIsLoading.set(false))
        )
        .subscribe({
          next: (registration) => {
            this.studentMeetDayStudent.set(registration);
          },
          error: () => {
            this.studentError.set("Student Registration Load Failed");
          },
        })
    );
  }

  private loadCompanyProfile(): void {
    this.subscriptions.add(
      this.sharedCompanyService.apiGetCompanyCurrent().subscribe({
        next: (response: any) => {
          const company = Array.isArray(response) ? response[0] : response;
          if (company?.id) {
            this.companyId = company.id;
            this.processAttendanceConfirmation();
          } else {
            this.handleCompanyError("Unable to load company profile.");
          }
        },
        error: () => {
          this.handleCompanyError("Failed to load company profile.");
        },
      })
    );
  }

  private processAttendanceConfirmation(): void {
    const meetDayId = this.route.snapshot.paramMap.get("meetDayId");
    const studentId = this.route.snapshot.queryParamMap.get("studentId");

    if (!meetDayId || !studentId) {
      this.handleCompanyError(
        "Invalid QR code. Missing meetday or student information."
      );
      return;
    }

    // For company flow: connect first, then fetch data
    // For admin flow: fetch data first, then confirm attendance
    if (this.isCompanyFlow() && this.companyId) {
      this.connectCompanyToStudent(this.companyId, studentId, meetDayId);
    } else {
      this.confirmAdminAttendance(meetDayId, studentId);
    }
  }

  private confirmAdminAttendance(meetDayId: string, studentId: string): void {
    this.subscriptions.add(
      forkJoin({
        meetDay: this.meetDayService.getMeetDay({ id: meetDayId }),
        student: this.studentService.apiGetStudentprofile(studentId),
      })
        .pipe(
          switchMap((result) => {
            this.meetDay.set(result.meetDay);
            this.student.set(result.student as ModelStudentprofile);
            return this.meetDayService.confirmAttendance({
              meetDayId: meetDayId,
              profileId: studentId,
            });
          })
        )
        .subscribe({
          next: () => {
            this.companyIsSuccess.set(true);
            this.companyIsLoading.set(false);
          },
          error: () => {
            this.handleCompanyError(
              "Failed to confirm attendance. The student may already be registered or there was an error."
            );
          },
        })
    );
  }

  private connectCompanyToStudent(
    companyId: string,
    studentId: string,
    meetDayId: string
  ): void {
    this.subscriptions.add(
      this.companyService
        .connectStudentWithCompany({
          id: companyId,
          studentProfileId: studentId,
        })
        .pipe(
          switchMap(() =>
            forkJoin({
              meetDay: this.meetDayService.getMeetDay({ id: meetDayId }),
              student: this.studentService.apiGetStudentprofile(studentId),
            })
          )
        )
        .subscribe({
          next: (result) => {
            this.meetDay.set(result.meetDay);
            this.student.set(result.student as ModelStudentprofile);
            this.companyIsSuccess.set(true);
            this.companyIsLoading.set(false);
          },
          error: () => {
            this.companyIsSuccess.set(true);
            this.companyIsLoading.set(false);
          },
        })
    );
  }

  private handleCompanyError(message: string): void {
    this.companyErrorMessage.set(message);
    this.companyIsLoading.set(false);
  }
}
